import { ChevronLeft, ChevronRight, ListTree, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface SidebarProps {
  activeView: string;
  treeViewContent?: React.ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ activeView, treeViewContent, collapsed = false, onToggleCollapse }: SidebarProps) {
  const showTree = (activeView === 'dashboard' || activeView === 'devices-review') && !!treeViewContent;

  // Render the tree in exactly ONE place — the inline desktop column OR the
  // mobile drawer — never both, so TreeView (which carries a ref and fires its
  // own data fetch) mounts a single time.
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Close the drawer whenever the tree is no longer relevant (e.g. navigating
  // to a form page) so it can't linger open over unrelated content.
  useEffect(() => {
    if (!showTree) setMobileOpen(false);
  }, [showTree]);

  // Nothing to show: don't reserve an empty column (previously a blank 14rem
  // strip on every form page, and dead width on a phone).
  if (!showTree) return null;

  // ---- Mobile: off-canvas drawer + floating toggle ----
  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open machine tree"
          className="md:hidden fixed bottom-4 left-4 z-40 flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full shadow-lg text-sm font-medium text-gray-700 active:scale-95 transition-transform"
        >
          <ListTree className="w-4 h-4 text-[#00D4BE]" />
          Tree
        </button>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <div className="relative z-10 w-[85vw] max-w-xs bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 flex flex-col shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <span className="text-sm font-semibold text-gray-700">Machines</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close"
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-auto px-3 py-4">
                <div className="w-max min-w-full">
                  {treeViewContent}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ---- Desktop: inline collapsible column ----
  return (
    <div className={`${collapsed ? 'w-16' : 'w-auto min-w-[14rem]'} shrink-0 bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 flex flex-col transition-[width] duration-300 relative z-40`}>
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          className="absolute top-6 -right-3 z-10 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>
      )}

      {!collapsed && (
        <div className="flex-1 overflow-y-auto overflow-x-auto px-3 py-4">
          <div className="w-max min-w-full">
            {treeViewContent}
          </div>
        </div>
      )}
    </div>
  );
}
